import { useEffect, useState, useMemo } from 'react';
import { useForm } from '@/contexts/FormContext';
import styles from './DebugPanel.module.css';

interface ProductChange {
  id: string;
  type: 'added' | 'removed' | 'replaced' | 'bonus';
  products: string[];
  replacedBy?: string;
  points?: number;
  timestamp: Date;
}

export const DebugPanel = () => {
  const { answers, totalPoints, getProductRecommendations, getBonusProducts, currentStep } =
    useForm();

  const [changes, setChanges] = useState<ProductChange[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);

  // Memoize product calculations to avoid infinite re-renders
  // Depend on answers and totalPoints to update when form state changes
  const currentProducts = useMemo(() => getProductRecommendations(), [answers, totalPoints]);
  const bonusProducts = useMemo(() => getBonusProducts(), [answers, totalPoints]);
  const regularProducts = useMemo(
    () => currentProducts.filter(p => !bonusProducts.includes(p)),
    [currentProducts, bonusProducts]
  );

  // Track product changes for logging only
  useEffect(() => {
    // Log current state
    console.log('🔍 Debug Panel Update:', {
      totalPoints,
      productsCount: currentProducts.length,
      bonusCount: bonusProducts.length,
      step: currentStep,
    });
  }, [totalPoints, currentStep, currentProducts.length, bonusProducts.length]);

  // Track detailed changes from ProductEngine events
  useEffect(() => {
    const handleProductChange = (event: CustomEvent) => {
      const { type, data } = event.detail;

      const change: ProductChange = {
        id: `${Date.now()}-${Math.random()}`,
        type: type as any,
        products: data.products || [],
        replacedBy: data.replacedBy,
        points: data.points,
        timestamp: new Date(),
      };

      setChanges(prev => [...prev, change]);
    };

    // Listen to ProductEngine events
    window.addEventListener('pemlab-product-added', handleProductChange as any);
    window.addEventListener('pemlab-product-removed', handleProductChange as any);
    window.addEventListener('pemlab-product-replaced', handleProductChange as any);
    window.addEventListener('pemlab-bonus-added', handleProductChange as any);

    return () => {
      window.removeEventListener('pemlab-product-added', handleProductChange as any);
      window.removeEventListener('pemlab-product-removed', handleProductChange as any);
      window.removeEventListener('pemlab-product-replaced', handleProductChange as any);
      window.removeEventListener('pemlab-bonus-added', handleProductChange as any);
    };
  }, []);

  return (
    <div className={`${styles.debugPanel} ${isMinimized ? styles.minimized : ''}`}>
      <div className={styles.header}>
        <h3>🔍 Debug Panel</h3>
        <button className={styles.toggleBtn} onClick={() => setIsMinimized(!isMinimized)}>
          {isMinimized ? '◀' : '▶'}
        </button>
      </div>

      {!isMinimized && (
        <div className={styles.content}>
          {/* Current Status */}
          <div className={styles.section}>
            <h4>📊 État actuel</h4>
            <div className={styles.stats}>
              <div className={styles.stat}>
                <span className={styles.label}>Points totaux:</span>
                <span className={styles.value}>{totalPoints}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.label}>Étape:</span>
                <span className={styles.value}>{currentStep + 1}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.label}>Réponses:</span>
                <span className={styles.value}>{answers.size}</span>
              </div>
            </div>
          </div>

          {/* Product Lists */}
          <div className={styles.section}>
            <h4>📦 Produits finaux ({currentProducts.length})</h4>
            <div className={styles.productList}>
              <div className={styles.subsection}>
                <h5>Produits réguliers ({regularProducts.length})</h5>
                {regularProducts.length > 0 ? (
                  <ul className={styles.products}>
                    {regularProducts.map((product, idx) => (
                      <li key={idx} className={styles.productItem}>
                        {product}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.empty}>Aucun produit</p>
                )}
              </div>

              <div className={styles.subsection}>
                <h5>🎁 Produits bonus ({bonusProducts.length})</h5>
                {bonusProducts.length > 0 ? (
                  <ul className={styles.products}>
                    {bonusProducts.map((product, idx) => (
                      <li key={idx} className={`${styles.productItem} ${styles.bonus}`}>
                        {product}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.empty}>Aucun bonus débloqué</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Changes */}
          <div className={styles.section}>
            <h4>📝 Changements récents</h4>
            <div className={styles.changes}>
              {changes.length > 0 ? (
                changes
                  .slice(-10)
                  .reverse()
                  .map(change => (
                    <div key={change.id} className={`${styles.change} ${styles[change.type]}`}>
                      <span className={styles.changeType}>
                        {change.type === 'added' && '➕ Ajouté'}
                        {change.type === 'removed' && '➖ Supprimé'}
                        {change.type === 'replaced' && '🔄 Remplacé'}
                        {change.type === 'bonus' && '🎁 Bonus'}
                      </span>
                      <span className={styles.changeProducts}>
                        {change.products.join(', ')}
                        {change.replacedBy && ` → ${change.replacedBy}`}
                        {change.points !== undefined && ` (+${change.points} pts)`}
                      </span>
                      <span className={styles.changeTime}>
                        {change.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  ))
              ) : (
                <p className={styles.empty}>Aucun changement</p>
              )}
            </div>
          </div>

          {/* Clear button */}
          <button className={styles.clearBtn} onClick={() => setChanges([])}>
            🗑️ Effacer l'historique
          </button>
        </div>
      )}
    </div>
  );
};
